import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Copy, Loader2, Smartphone } from 'lucide-react';
import * as QRCode from 'qrcode';
import { Button, Modal } from './UIComponents';
import { handleApiError } from '../api/client';
import { createUploadLink, getUploadLink, type UploadLinkResponse } from '../api/uploadLinks';

interface PhoneUploadLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EXPIRY_OPTIONS = [
  { label: '15 minutes (default)', value: '15m' },
  { label: '30 minutes', value: '30m' },
  { label: '1 hour', value: '1h' },
] as const;

const PhoneUploadLinkModal: React.FC<PhoneUploadLinkModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [expiresIn, setExpiresIn] = useState<string>('15m');
  const [link, setLink] = useState<UploadLinkResponse | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<number | null>(null);
  const didAutoNavigateRef = useRef(false);
  const autoNavigateTimerRef = useRef<number | null>(null);

  const isCompleted = Boolean(link?.used_at && link?.transcription_id);
  const isLocalhostLink = useMemo(() => {
    if (!link?.url) return false;
    try {
      const parsed = new URL(link.url);
      return parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    } catch {
      return false;
    }
  }, [link?.url]);

  const title = useMemo(() => (isCompleted ? 'Upload complete' : 'Upload from phone'), [isCompleted]);

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setLink(null);
    setQrDataUrl('');
    setCopied(false);
    setIsLoading(true);

    (async () => {
      try {
        const created = await createUploadLink({ expires_in: expiresIn });
        setLink(created);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [expiresIn, isOpen]);

  useEffect(() => {
    if (!isOpen || !link?.url) return;
    let cancelled = false;
    (async () => {
      try {
        const dataUrl = await QRCode.toDataURL(link.url, { margin: 1, width: 240 });
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch {
        if (!cancelled) setQrDataUrl('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, link?.url]);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) {
        window.clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = null;
      }
      if (autoNavigateTimerRef.current) {
        window.clearTimeout(autoNavigateTimerRef.current);
        autoNavigateTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !link?.id) return;
    if (isCompleted) return;

    const interval = window.setInterval(async () => {
      try {
        const updated = await getUploadLink(link.id);
        setLink(updated);
      } catch {
        // ignore polling errors
      }
    }, 2000);

    return () => window.clearInterval(interval);
  }, [isCompleted, isOpen, link?.id]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isCompleted) return;
    if (!link?.transcription_id) return;
    if (didAutoNavigateRef.current) return;

    didAutoNavigateRef.current = true;
    autoNavigateTimerRef.current = window.setTimeout(() => {
      navigate(`/transcription/${link.transcription_id}`);
      onClose();
    }, 600);
  }, [isCompleted, isOpen, link?.transcription_id, navigate, onClose]);

  const copyLink = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // ignore
    }
  };

  const footer = (
    <>
      {!isCompleted ? (
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button
            onClick={copyLink}
            disabled={!link?.url}
            className={copied ? 'bg-emerald-600 hover:bg-emerald-600 transition-transform duration-200 scale-[1.02]' : ''}
          >
            {copied ? (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Copy link
              </>
            )}
          </Button>
        </>
      ) : (
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button
            onClick={() => {
              if (link?.transcription_id) navigate(`/transcription/${link.transcription_id}`);
            }}
            disabled={!link?.transcription_id}
          >
            Open transcription
          </Button>
        </>
      )}
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} footer={footer}>
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-indigo-600/10 flex items-center justify-center border border-indigo-200/50 dark:border-indigo-500/20 dark:bg-indigo-500/10">
            {isCompleted ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <Smartphone className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm text-slate-600 dark:text-zinc-400">
              Scan the QR code with your phone to upload one audio file. After confirmation, transcription starts automatically.
            </p>
          </div>
        </div>

        {!isCompleted && (
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-slate-900 dark:text-zinc-100">
              Link expiration
            </label>
            <select
              className="w-full rounded-lg border-slate-200 dark:border-zinc-700 py-2.5 px-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-slate-50 dark:bg-zinc-800 dark:text-zinc-100 hover:bg-white dark:hover:bg-zinc-700 transition-colors cursor-pointer"
              value={expiresIn}
              onChange={(e) => setExpiresIn(e.target.value)}
              disabled={isLoading}
            >
              {EXPIRY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-zinc-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Generating link…
          </div>
        )}

        {link?.url && (
          <div className="space-y-3">
            {isLocalhostLink && (
              <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-300">
                This link uses <span className="font-semibold">localhost</span>, which won&apos;t work on your phone.
                Open the app using your computer&apos;s LAN IP (e.g. <span className="font-mono">http://192.168.x.x:3000</span>) and generate the QR again.
              </div>
            )}

            <div className="flex items-center justify-center">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="QR code"
                  className="h-60 w-60 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white"
                />
              ) : (
                <div className="h-60 w-60 rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900 flex items-center justify-center text-slate-500 dark:text-zinc-400 text-sm">
                  QR unavailable
                </div>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900 p-3">
              <p className="text-xs text-slate-500 dark:text-zinc-400 mb-1">Link</p>
              <p className="text-sm text-slate-900 dark:text-zinc-100 break-all">{link.url}</p>
            </div>

            {!isCompleted && (
              <p className="text-xs text-slate-500 dark:text-zinc-400">
                Waiting for upload… keep this dialog open to auto-detect completion.
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PhoneUploadLinkModal;
